import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { useSetFilters } from '@/legacy-src/components/filters/hooks/useSetFilters'

import { VARIANT_IN_PARAMS_SLUG } from '../../../lib/constants'
import { FILTER_ALL_SLUGS } from '../filters.constants'
import { type FilterSlug } from '../filters.interface'
import { type SortSlug } from '../sort.interface'

type Filter = { slug: FilterSlug | 'service'; value: string | string[] }

const removeExperimentFromParams = (params: URLSearchParams) => {
  // these are added to the url because of how the url is rewritten in middleware.ts
  params.delete('therapists')
  params.delete(VARIANT_IN_PARAMS_SLUG)
}

const removeAllFiltersFromParams = (params: URLSearchParams) => {
  FILTER_ALL_SLUGS.forEach((slug) => {
    params.delete(slug)
  })
}

const useFilters = () => {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const setFilters = useSetFilters()

  const getFilterValue = useCallback(
    (slug: FilterSlug | SortSlug) => {
      const urlValue = searchParams.get(slug)
      return urlValue?.split(',') || []
    },
    [searchParams],
  )

  const resetAllFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams)

    removeExperimentFromParams(params)
    removeAllFiltersFromParams(params)

    router.push(pathname + '?' + params.toString(), undefined, { shallow: true })
  }, [searchParams])

  const { activeFilters, activeFiltersArray, hasActiveFilters } = useMemo(() => {
    const activeFilters = FILTER_ALL_SLUGS.reduce((accumulator, slug) => {
      if (searchParams?.get(slug)) {
        accumulator[slug] = searchParams.get(slug)?.split(',')
      }

      return accumulator
    }, {})

    const activeFiltersArray = Object.keys(activeFilters).map((key) => ({
      slug: key,
      value: activeFilters[key],
    })) as Filter[]

    const hasActiveFilters = Boolean(FILTER_ALL_SLUGS.filter((slug) => activeFilters[slug]).length)

    return { activeFilters, activeFiltersArray, hasActiveFilters }
  }, [searchParams])

  return {
    setFilters,
    resetAllFilters,
    activeFilters,
    activeFiltersArray,
    getFilterValue,
    hasActiveFilters,
  }
}

export default useFilters
